<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { Select } from "@/components/episto-ui/select";
import OptionRow from "./OptionRow.vue";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { metricTypesFor } from "@/composables/kpiMetric";

const props = defineProps({
  metric: { type: Object, required: true },
});

const emit = defineEmits(["update"]);

const ELIGIBLE_TYPES = [
  "q_single", "q_multiple", "q_rating", "q_slider", "q_nps",
  "q_image_choice", "q_image_choice_multiple",
];

const METRIC_TYPE_OPTIONS = [
  { value: "options_share", label: "% of answers" },
  { value: "average", label: "Average" },
  { value: "nps", label: "NPS" },
];

const store = useResultsV2Store();
const { availableCards, currentView } = storeToRefs(store);
const localesStore = useSurveyLocalesStore();
const { analysisLocale } = storeToRefs(localesStore);

const displayLocale = computed(
  () => currentView.value?.options?.locale_token || analysisLocale.value?.token || null,
);

const questionOptions = computed(() =>
  availableCards.value
    .filter((c) => ELIGIBLE_TYPES.includes(c.type))
    .map((c) => ({ value: c.questions?.[0]?.id, label: resolveLocalizedTitle(c.name || c.token, displayLocale.value) }))
    .filter((o) => o.value != null),
);

function metaForQuestion(questionId) {
  return availableCards.value.find((c) => (c.questions || []).some((q) => q.id === questionId)) || null;
}

const selectedMeta = computed(() => metaForQuestion(props.metric.question_id));

function allowedTypesFor(meta) {
  return metricTypesFor(meta?.type);
}

const metricTypeOptions = computed(() => {
  const allowed = allowedTypesFor(selectedMeta.value);
  return METRIC_TYPE_OPTIONS.filter((o) => allowed.includes(o.value));
});

function onQuestionChange(questionId) {
  const allowed = allowedTypesFor(metaForQuestion(questionId));
  const type = allowed.includes(props.metric.type) ? props.metric.type : "options_share";
  patch({ question_id: questionId, type, option_codes: [] });
}

function answerOptionsFor(meta) {
  if (!meta) return [];
  const s = meta.specs || {};
  const fromAnswers = (answers) =>
    (answers || []).map((a, i) => ({
      value: String(i + 1),
      label: resolveLocalizedTitle(a.title, displayLocale.value) || String(i + 1),
    }));
  const fromRange = (min, max) => {
    const out = [];
    for (let n = min; n <= max; n++) out.push({ value: String(n), label: String(n) });
    return out;
  };
  if (["q_single", "q_multiple", "q_image_choice", "q_image_choice_multiple"].includes(meta.type)) {
    return fromAnswers(s.answers);
  }
  if (meta.type === "q_rating") return fromRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
  if (meta.type === "q_slider") return fromRange(s.slider_limits?.min ?? 0, s.slider_limits?.max ?? 10);
  if (meta.type === "q_nps") return fromRange(0, 10);
  return [];
}

const answerOptions = computed(() => answerOptionsFor(selectedMeta.value));

function patch(p) {
  emit("update", { ...props.metric, ...p });
}
</script>

<template>
  <OptionRow
    label="Metric"
    hint="The value displayed: a share of answers, an average or an NPS on the question you pick.">
    <div class="space-y-2">
      <Select
      :model-value="metric.question_id"
      :options="questionOptions"
      placeholder="Select a question..."
      filterable
      @update:model-value="onQuestionChange($event)" />
    <Select
      :model-value="metric.type"
      :options="metricTypeOptions"
      @update:model-value="patch({ type: $event, option_codes: $event === 'options_share' ? metric.option_codes || [] : [] })" />
    <Select
      v-if="metric.type === 'options_share'"
      :model-value="metric.option_codes || []"
      :options="answerOptions"
      placeholder="Pick answer options..."
      multiple
      @update:model-value="patch({ option_codes: $event })" />
    </div>
  </OptionRow>
</template>
